import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, MapPin, Calendar, User, CreditCard, Printer, Phone, Mail, FileText, Hotel, Bed, Receipt, AlertCircle, Eye, EyeOff, Plus, Send, Download, Filter, ChevronDown, TrendingUp, Wallet, BarChart3, Moon, History, UserCheck, Trash2, RefreshCw, Undo2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatTRY, formatDate, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSiteContent } from "@/lib/siteContent";
import AdminLayout from "@/components/AdminLayout";

const STATUSES = [
    { value: "pending", label: "Beklemede" },
    { value: "confirmed", label: "Onaylandı" },
    { value: "cancelled", label: "İptal" },
    { value: "completed", label: "Tamamlandı" },
    { value: "rights_reserved", label: "Hakkı Saklı" },
    { value: "refund_pending", label: "İade Bekliyor" },
    { value: "refunded", label: "İade Edildi" },
];
const STATUS_BADGE = {
    pending: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300",
    confirmed: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300",
    cancelled: "bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300",
    completed: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-500/10 dark:text-slate-300",
    rights_reserved: "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-500/10 dark:text-violet-300",
    refund_pending: "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-500/10 dark:text-orange-300",
    refunded: "bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-500/10 dark:text-sky-300",
};
const STATUS_LABEL = Object.fromEntries(STATUSES.map(s => [s.value, s.label]));

const PAYMENT_STATUSES = [
    { value: "", label: "Tüm Ödemeler" },
    { value: "initiated", label: "Başlatıldı" },
    { value: "paid", label: "Ödendi" },
    { value: "partial", label: "Kısmi" },
    { value: "failed", label: "Başarısız" },
    { value: "refunded", label: "İade" },
];

const SOURCES = [
    { value: "", label: "Tüm Kaynaklar" },
    { value: "direct", label: "Direkt" },
    { value: "agency", label: "Acente" },
    { value: "phone", label: "Telefon" },
];

const PAYMENT_METHODS = [
    { value: "card", label: "Kredi Kartı" },
    { value: "bank_transfer", label: "Havale/EFT" },
    { value: "at_hotel", label: "Otelde Ödeme" },
    { value: "cash", label: "Nakit" },
    { value: "stripe", label: "Stripe" },
    { value: "other", label: "Diğer" },
];
const PM_LABEL = { card: "Kredi Kartı", bank_transfer: "Havale/EFT", at_hotel: "Otelde Ödeme", cash: "Nakit", stripe: "Stripe", other: "Diğer" };

const DATE_FIELDS = [
    { value: "created_at", label: "Oluşturma" },
    { value: "check_in", label: "Giriş" },
    { value: "check_out", label: "Çıkış" },
];

const PRESETS = [
    { key: "today", label: "Bugün giriş", apply: (d) => { const t = ymd(d); return { date_field: "check_in", date_from: t, date_to: t }; } },
    { key: "week", label: "Bu hafta giriş", apply: (d) => { const dow = d.getDay() || 7; const m = new Date(d); m.setDate(d.getDate() - dow + 1); const s = new Date(m); s.setDate(m.getDate() + 6); return { date_field: "check_in", date_from: ymd(m), date_to: ymd(s) }; } },
    { key: "month", label: "Bu ay rezerve", apply: (d) => { const s = new Date(d.getFullYear(), d.getMonth(), 1); const e = new Date(d.getFullYear(), d.getMonth() + 1, 0); return { date_field: "created_at", date_from: ymd(s), date_to: ymd(e) }; } },
    { key: "30d", label: "Son 30 gün", apply: (d) => { const s = new Date(d); s.setDate(d.getDate() - 30); return { date_field: "created_at", date_from: ymd(s), date_to: ymd(d) }; } },
];

const ymd = (d) => d.toISOString().slice(0, 10);

export default function AdminBookingsPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState({ count: 0, nights: 0, sales: 0, cost: 0, profit: 0, paid: 0, remaining: 0 });
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 50;
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [paymentStatus, setPaymentStatus] = useState("");
    const [source, setSource] = useState("");
    const [paymentMethod, setPaymentMethod] = useState(""); // "" | "card,bank_transfer,..." (comma list)
    const [selectedHotelIds, setSelectedHotelIds] = useState([]);
    const [dateField, setDateField] = useState("created_at");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [minTotal, setMinTotal] = useState("");
    const [maxTotal, setMaxTotal] = useState("");
    const [hotelsList, setHotelsList] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [detail, setDetail] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [hotel, setHotel] = useState(null);
    const [voucherModal, setVoucherModal] = useState(false); // options modal
    const [voucherView, setVoucherView] = useState(false); // showing rendered voucher
    const [paymentModal, setPaymentModal] = useState(false);
    const [emailModal, setEmailModal] = useState(false);
    // Satır butonlarından açılan hızlı modallarda hedef booking (drawer'ı açmaz)
    const [quickTargetBooking, setQuickTargetBooking] = useState(null);
    const [showAuditLog, setShowAuditLog] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [voucherOpts, setVoucherOpts] = useState({
        showPrice: true,
        showPayment: true,
        showAdminNote: false,
        showHotelContact: true,
        showStripeRef: false,
        showGuestPhone: true,
        showSpecialRequests: true,
    });

    const [ratePlans, setRatePlans] = useState([]);
    const [staffList, setStaffList] = useState([]);
    // Auto-refresh state
    const [highlightIds, setHighlightIds] = useState([]);
    const [lastRefresh, setLastRefresh] = useState(null);

    // Hakkı Saklı: müşteri bakiyesi içeride kalsın, iade yok
    const handleRightsReserved = async () => {
        if (!detail) return;
        const paid = (detail.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
        const note = window.prompt(`Hakkı Saklı işaretleniyor.\n\nÖdenen tutar: ₺${paid.toFixed(2)}\nBu tutar müşterinin bakiyesine eklenecek (iade YAPILMAYACAK).\n\nNot ekleyin (opsiyonel):`, "");
        if (note === null) return;
        try {
            const r = await api.post(`/admin/bookings/${detail.id}/rights-reserved`, { note });
            toast.success(`Hakkı Saklı işlendi. Müşteri bakiyesine ₺${(r.data?.credit_added || 0).toFixed(2)} eklendi.`);
            setDetail(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message || "Hata");
        }
    };

    // İade İste: Finans modülüne kuyruğa atar
    const handleRefundRequest = async () => {
        if (!detail) return;
        const paid = (detail.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
        const amountStr = window.prompt(`İade tutarını girin (₺):\n\nÖdenen tutar: ₺${paid.toFixed(2)}\nMaksimum iade: ₺${paid.toFixed(2)}`, String(paid.toFixed(2)));
        if (amountStr === null) return;
        const amount = Number(String(amountStr).replace(",", "."));
        if (!amount || amount <= 0) { toast.error("Geçersiz tutar"); return; }
        if (amount > paid + 0.01) { toast.error(`En fazla ₺${paid.toFixed(2)} iade edebilirsiniz`); return; }
        const reason = window.prompt("İade nedeni:", "");
        if (reason === null) return;
        try {
            await api.post(`/admin/bookings/${detail.id}/refund-request`, { amount, reason });
            toast.success(`İade talebi finans modülüne gönderildi (₺${amount.toFixed(2)}). Finans gerçekleştirince rezervasyon otomatik 'İade Edildi' olur.`);
            setDetail(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message || "Hata");
        }
    };

    // Staff list (sales agents) — loaded once when the page mounts so the
    // edit form can show a select instead of a free-text input.
    useEffect(() => {
        api.get("/admin/users", { params: { role: "staff", limit: 200 } })
            .then((r) => setStaffList(r.data?.items || []))
            .catch(() => setStaffList([]));
    }, []);

    useEffect(() => {
        if (!detail) { setHotel(null); setRatePlans([]); setEditMode(false); setEditForm(null); setVoucherView(false); setVoucherModal(false); setShowAuditLog(false); setAuditLogs([]); return; }
        api.get(`/admin/hotels/${detail.hotel_id}`).then((r) => setHotel(r.data)).catch(() => setHotel(null));
        api.get(`/admin/hotels/${detail.hotel_id}/rate-plans`).then((r) => setRatePlans(r.data || [])).catch(() => setRatePlans([]));
    }, [detail]);

    useEffect(() => {
        if (!showAuditLog || !detail) return;
        api.get(`/admin/bookings/${detail.id}/audit-log`)
            .then((r) => setAuditLogs(r.data || []))
            .catch(() => setAuditLogs([]));
    }, [showAuditLog, detail]);

    const startEdit = () => {
        setEditForm({
            check_in: detail.check_in,
            check_out: detail.check_out,
            adults: detail.adults,
            children: detail.children,
            child_ages: detail.child_ages || [],
            rooms: detail.rooms,
            room_type_id: detail.room_type_id,
            rate_plan_id: detail.rate_plan_id || "",
            subtotal: detail.subtotal,
            cost_amount: detail.cost_amount,
            commission_amount: detail.commission_amount,
            insurance_amount: detail.insurance_amount,
            total_amount: detail.total_amount,
            paid_amount: detail.paid_amount ?? 0,
            status: detail.status,
            payment_status: detail.payment_status,
            payment_method: detail.payment_method || "",
            source: detail.source || "online",
            sales_agent_id: detail.sales_agent_id || "",
            sales_agent_name: detail.sales_agent_name || "",
            guest_first_name: detail.guest.first_name,
            guest_last_name: detail.guest.last_name,
            guest_email: detail.guest.email,
            guest_phone: detail.guest.phone,
            special_requests: detail.special_requests || "",
            admin_note: detail.admin_note || "",
        });
        setEditMode(true);
    };

    const saveEdit = async () => {
        try {
            const r = await api.put(`/admin/bookings/${detail.id}/full`, editForm);
            setDetail(r.data);
            setEditMode(false);
            toast.success("Rezervasyon güncellendi");
            // Refresh full list + summary KPIs so the table row and top cards reflect changes.
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Güncelleme başarısız");
        }
    };

    const buildParams = () => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (selectedStatuses.length) params.set("statuses", selectedStatuses.join(","));
        if (paymentStatus) params.set("payment_status", paymentStatus);
        if (source) params.set("source", source);
        if (paymentMethod) params.set("payment_method", paymentMethod);
        if (selectedHotelIds.length) params.set("hotel_ids", selectedHotelIds.join(","));
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
        if (dateField) params.set("date_field", dateField);
        if (minTotal) params.set("min_total", minTotal);
        if (maxTotal) params.set("max_total", maxTotal);
        return params;
    };

    const load = (silent = false) => {
        if (!silent) setLoading(true);
        const params = buildParams();
        params.set("limit", PAGE_SIZE);
        params.set("skip", (page - 1) * PAGE_SIZE);
        api.get(`/admin/bookings?${params.toString()}`)
            .then((r) => {
                // Highlight rows whose hotel_confirm_status changed since last poll
                if (silent) {
                    const prevMap = new Map((items || []).map((x) => [x.id, x.hotel_confirm_status]));
                    const changed = [];
                    (r.data.items || []).forEach((nb) => {
                        const prev = prevMap.get(nb.id);
                        if (prev !== undefined && prev !== nb.hotel_confirm_status) changed.push(nb.id);
                    });
                    if (changed.length) {
                        setHighlightIds(changed);
                        setTimeout(() => setHighlightIds([]), 2500);
                    }
                }
                setItems(r.data.items);
                setTotal(r.data.total);
                setSummary(r.data.summary || summary);
                setLastRefresh(new Date());
            })
            .finally(() => { if (!silent) setLoading(false); });
    };

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/login");
        load();
        // Hotels list for filter dropdown
        api.get("/admin/hotels?limit=200").then((r) => setHotelsList(r.data.items || r.data || [])).catch(() => {});
    }, [user, authLoading]); // eslint-disable-line

    useEffect(() => {
        const t = setTimeout(load, 350);
        return () => clearTimeout(t);
    }, [q, selectedStatuses, paymentStatus, source, paymentMethod, selectedHotelIds, dateField, dateFrom, dateTo, minTotal, maxTotal, page]); // eslint-disable-line

    // Silent auto-refresh (30s) — picks up hotel confirm/reject responses without a full reload.
    useEffect(() => {
        if (authLoading || !user || !["super_admin", "admin"].includes(user.role)) return;
        const t = setInterval(() => {
            // Skip while a drawer / modal is open to avoid interrupting user
            if (detail || voucherModal || emailModal) return;
            if (document.hidden) return;
            load(true);
        }, 30000);
        return () => clearInterval(t);
    }, [authLoading, user, detail, voucherModal, emailModal, q, selectedStatuses, paymentStatus, source, paymentMethod, selectedHotelIds, dateField, dateFrom, dateTo, minTotal, maxTotal, page]); // eslint-disable-line

    // Filter değişince sayfayı 1'e döndür
    useEffect(() => {
        setPage(1);
    }, [q, selectedStatuses, paymentStatus, source, paymentMethod, selectedHotelIds, dateField, dateFrom, dateTo, minTotal, maxTotal]);

    const exportFile = (kind) => {
        const params = buildParams();
        if (selectedIds.length) params.set("ids", selectedIds.join(","));
        const url = `${api.defaults.baseURL}/admin/bookings/export.${kind}?${params.toString()}`;
        // Use fetch with auth header, then download
        const token = localStorage.getItem("ms_token");
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((b) => {
                const a = document.createElement("a");
                const objUrl = URL.createObjectURL(b);
                a.href = objUrl;
                a.download = `rezervasyonlar_${new Date().toISOString().slice(0, 10)}.${kind === "xlsx" ? "xlsx" : "csv"}`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(objUrl);
            });
    };

    const applyPreset = (presetKey) => {
        const p = PRESETS.find((x) => x.key === presetKey);
        if (!p) return;
        const v = p.apply(new Date());
        setDateField(v.date_field);
        setDateFrom(v.date_from);
        setDateTo(v.date_to);
    };

    const clearFilters = () => {
        setQ("");
        setSelectedStatuses([]);
        setPaymentStatus("");
        setSource("");
        setPaymentMethod("");
        setSelectedHotelIds([]);
        setDateField("created_at");
        setDateFrom("");
        setDateTo("");
        setMinTotal("");
        setMaxTotal("");
    };

    const toggleSelect = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
    const toggleSelectAll = () => setSelectedIds((s) => s.length === items.length ? [] : items.map((b) => b.id));

    const bulkAction = async (action, extra = {}) => {
        if (selectedIds.length === 0) return toast.error("Önce rezervasyon seçin");
        try {
            const r = await api.post("/admin/bookings/bulk-action", { ids: selectedIds, action, ...extra });
            toast.success(`${r.data.updated} kayıt güncellendi`);
            setSelectedIds([]);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Hata");
        }
    };

    const updateStatus = async (id, payload) => {
        try {
            const r = await api.patch(`/admin/bookings/${id}`, payload);
            setItems((prev) => prev.map((b) => b.id === id ? r.data : b));
            if (detail?.id === id) setDetail(r.data);
            // Friendly toast
            if (payload.status) {
                const labels = { pending: "Beklemede", confirmed: "Onaylandı ✓", cancelled: "İptal edildi" };
                toast.success(`Durum güncellendi: ${labels[payload.status] || payload.status}`);
            } else if (payload.payment_status) {
                const labels = { initiated: "Bekliyor", paid: "Ödendi ✓", partial: "Parçalı ödeme", failed: "Başarısız", refunded: "İade edildi" };
                toast.success(`Ödeme durumu: ${labels[payload.payment_status] || payload.payment_status}`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Güncelleme başarısız");
        }
    };

    const addPayment = async (payload) => {
        try {
            const r = await api.post(`/admin/bookings/${detail.id}/payments`, payload);
            setDetail(r.data);
            setItems((prev) => prev.map((b) => b.id === detail.id ? r.data : b));
            setPaymentModal(false);
            toast.success(`Ödeme eklendi: ${payload.amount} ₺`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eklenemedi");
        }
    };

    const deletePayment = async (paymentId) => {
        if (!window.confirm("Bu ödeme kaydını silmek istediğinize emin misiniz? İşlem geçmişe loglanır.")) return;
        try {
            const r = await api.delete(`/admin/bookings/${detail.id}/payments/${paymentId}`);
            setDetail(r.data);
            setItems((prev) => prev.map((b) => b.id === detail.id ? r.data : b));
            toast.success("Ödeme silindi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        }
    };

    const sendEmail = async (payload) => {
        const targetId = (detail && detail.id) || (quickTargetBooking && quickTargetBooking.id);
        if (!targetId) { toast.error("Rezervasyon bulunamadı"); return; }
        try {
            const r = await api.post(`/admin/bookings/${targetId}/send-email`, payload);
            setEmailModal(false);
            setQuickTargetBooking(null);
            if (r.data.status === "sent") toast.success(`E-posta gönderildi: ${r.data.to}`);
            else if (r.data.status === "pending_setup") toast.info(`Kuyruğa alındı (SMTP henüz aktif değil) → ${r.data.to}`);
            else if (r.data.status === "failed") toast.error(`Gönderim hatası: ${r.data.error}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Gönderilemedi");
        }
    };

    return (
        <AdminLayout title="Rezervasyonlar">
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
                <SummaryCard icon={<Receipt className="h-3.5 w-3.5" />} label="Rezervasyon" value={summary.count} color="slate" />
                <SummaryCard icon={<Moon className="h-3.5 w-3.5" />} label="Geceleme" value={summary.nights} color="indigo" />
                <SummaryCard icon={<TrendingUp className="h-3.5 w-3.5" />} label="Satış" value={formatTRY(summary.sales)} color="sky" />
                <SummaryCard icon={<BarChart3 className="h-3.5 w-3.5" />} label="Maliyet" value={formatTRY(summary.cost)} color="amber" />
                <SummaryCard icon={<TrendingUp className="h-3.5 w-3.5" />} label="Kar" value={formatTRY(summary.profit)} color="emerald" />
                <SummaryCard icon={<Wallet className="h-3.5 w-3.5" />} label="Tahsil" value={formatTRY(summary.paid)} color="emerald" />
                <SummaryCard icon={<Wallet className="h-3.5 w-3.5" />} label="Kalan" value={formatTRY(summary.remaining)} color="rose" />
            </div>

            {/* Filter bar */}
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 mb-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-[240px] px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                        <Search className="h-4 w-4 text-slate-400" />
                        <input data-testid="booking-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad, e-posta, tel, voucher no..." className="w-full bg-transparent outline-none text-sm" />
                        {q && <button onClick={() => setQ("")}><X className="h-3.5 w-3.5 text-slate-400" /></button>}
                    </div>
                    <button onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters-btn" className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition-colors ${showFilters ? "bg-sky-500 border-sky-500 text-white" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"}`}>
                        <Filter className="h-3.5 w-3.5" /> Gelişmiş Filtre
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showFilters ? "rotate-180" : ""}`} />
                    </button>
                    <div className="flex flex-wrap gap-1">
                        {PRESETS.map((p) => (
                            <button key={p.key} type="button" onClick={() => applyPreset(p.key)} data-testid={`preset-${p.key}`} className="px-3 py-2 rounded-xl text-xs font-semibold bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200">
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            onClick={() => load(false)}
                            data-testid="refresh-bookings-btn"
                            title={lastRefresh ? `Son yenileme: ${lastRefresh.toLocaleTimeString("tr-TR")}` : "Listeyi yenile"}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-sky-100 dark:bg-sky-950/40 hover:bg-sky-200 dark:hover:bg-sky-900/60 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-900"
                        >
                            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                            Yenile
                            {lastRefresh && (
                                <span className="hidden lg:inline text-[10px] font-medium opacity-70">
                                    · {lastRefresh.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                                </span>
                            )}
                        </button>
                        <button onClick={() => exportFile("xlsx")} data-testid="export-xlsx-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-emerald-500 hover:bg-emerald-600 text-white">
                            <Download className="h-3.5 w-3.5" /> Excel
                        </button>
                        <button onClick={() => exportFile("csv")} data-testid="export-csv-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200">
                            <Download className="h-3.5 w-3.5" /> CSV
                        </button>
                        <button onClick={() => window.print()} data-testid="print-list-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200">
                            <Printer className="h-3.5 w-3.5" /> Yazdır
                        </button>
                    </div>
                </div>

                {showFilters && (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                        <FilterField label="Tarih Alanı">
                            <select value={dateField} onChange={(e) => setDateField(e.target.value)} className={FLT_INPUT}>
                                {DATE_FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Başlangıç">
                            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={FLT_INPUT} data-testid="filter-date-from" />
                        </FilterField>
                        <FilterField label="Bitiş">
                            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={FLT_INPUT} data-testid="filter-date-to" />
                        </FilterField>
                        <FilterField label="Ödeme Durumu">
                            <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} className={FLT_INPUT}>
                                {PAYMENT_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Kaynak">
                            <select value={source} onChange={(e) => setSource(e.target.value)} className={FLT_INPUT}>
                                {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Tutar Aralığı (₺)">
                            <div className="flex gap-1">
                                <input type="number" placeholder="Min" value={minTotal} onChange={(e) => setMinTotal(e.target.value)} className={FLT_INPUT} />
                                <input type="number" placeholder="Max" value={maxTotal} onChange={(e) => setMaxTotal(e.target.value)} className={FLT_INPUT} />
                            </div>
                        </FilterField>
                        <div className="col-span-2 md:col-span-4 lg:col-span-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Durum</div>
                            <div className="flex flex-wrap gap-1">
                                {STATUSES.map((s) => (
                                    <button key={s.value} type="button" onClick={() => setSelectedStatuses((arr) => arr.includes(s.value) ? arr.filter((x) => x !== s.value) : [...arr, s.value])} className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${selectedStatuses.includes(s.value) ? "bg-sky-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"}`}>{s.label}</button>
                                ))}
                            </div>
                        </div>
                        <div className="col-span-2 md:col-span-4 lg:col-span-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Otel</div>
                            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                                {hotelsList.map((h) => (
                                    <button key={h.id} type="button" onClick={() => setSelectedHotelIds((arr) => arr.includes(h.id) ? arr.filter((x) => x !== h.id) : [...arr, h.id])} className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${selectedHotelIds.includes(h.id) ? "bg-sky-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"}`}>{h.name}</button>
                                ))}
                            </div>
                        </div>
                        <div className="col-span-2 md:col-span-4 lg:col-span-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Ödeme Tipi</div>
                            <div className="flex flex-wrap gap-1">
                                {PAYMENT_METHODS.map((pm) => {
                                    const arr = paymentMethod ? paymentMethod.split(",") : [];
                                    const active = arr.includes(pm.value);
                                    return (
                                        <button key={pm.value} type="button" data-testid={`filter-pm-${pm.value}`} onClick={() => {
                                            const next = active ? arr.filter((x) => x !== pm.value) : [...arr, pm.value];
                                            setPaymentMethod(next.join(","));
                                        }} className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${active ? "bg-violet-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"}`}>{pm.label}</button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="md:col-span-4 lg:col-span-6 flex justify-end">
                            <button onClick={clearFilters} data-testid="clear-filters-btn" className="text-xs font-semibold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">Filtreleri temizle</button>
                        </div>
                    </div>
                )}

                {selectedIds.length > 0 && (
                    <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-200">{selectedIds.length} seçili</span>
                        <button onClick={() => bulkAction("set_status", { status: "confirmed" })} className="px-3 py-1 rounded-lg bg-emerald-500 text-white text-[11px] font-semibold">Onayla</button>
                        <button onClick={() => bulkAction("set_status", { status: "cancelled" })} className="px-3 py-1 rounded-lg bg-red-500 text-white text-[11px] font-semibold">İptal</button>
                        <button onClick={() => bulkAction("set_source", { source: "agency" })} className="px-3 py-1 rounded-lg bg-purple-500 text-white text-[11px] font-semibold">→ Acente Kaynağı</button>
                        <button onClick={() => exportFile("xlsx")} className="px-3 py-1 rounded-lg bg-sky-500 text-white text-[11px] font-semibold inline-flex items-center gap-1">
                            <Download className="h-3 w-3" /> Seçilenleri Excel'e
                        </button>
                        <button onClick={() => setSelectedIds([])} className="ml-auto text-[11px] text-slate-500">Seçimi temizle</button>
                    </div>
                )}
            </div>

            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">
                                <th className="py-2 px-2">
                                    <input type="checkbox" checked={selectedIds.length === items.length && items.length > 0} onChange={toggleSelectAll} className="h-3.5 w-3.5 accent-sky-500" />
                                </th>
                                <th className="py-2 px-2">Voucher</th>
                                <th className="py-2 px-2">Otel · Oda · Plan</th>
                                <th className="py-2 px-2">Misafir</th>
                                <th className="py-2 px-2">Giriş - Çıkış</th>
                                <th className="py-2 px-2 text-center">Kişi</th>
                                <th className="py-2 px-2 text-right">Satış / Maliyet / Kar</th>
                                <th className="py-2 px-2 text-right">Ödenen / Kalan</th>
                                <th className="py-2 px-2">Ödeme Tipi</th>
                                <th className="py-2 px-2">Satışı Yapan</th>
                                <th className="py-2 px-2">Durum</th>
                                <th className="py-2 px-2 text-center">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="13" className="py-8 text-center text-slate-500">Yükleniyor...</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan="13" className="py-8 text-center text-slate-500">Kayıt bulunamadı.</td></tr>
                            ) : items.map((b) => {
                                const paymentsSum = (b.payments || []).reduce((s, p) => s + (p.amount || 0), 0);
                                const paid = paymentsSum > 0 ? paymentsSum : (b.paid_amount || 0);
                                const remaining = Math.max(0, (b.total_amount || 0) - paid);
                                const paidPct = (b.total_amount || 0) > 0 ? Math.min(100, Math.round(paid / b.total_amount * 100)) : 0;
                                return (
                                    <tr key={b.id} data-testid={`booking-row-${b.id}`} onClick={() => setDetail(b)} className={`border-b border-slate-100 dark:border-slate-800/40 hover:bg-slate-50 dark:hover:bg-slate-800/30 cursor-pointer transition-colors duration-500 ${highlightIds.includes(b.id) ? "bg-sky-100 dark:bg-sky-900/40 animate-pulse" : ""}`}>
                                        <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                                            <input type="checkbox" checked={selectedIds.includes(b.id)} onChange={() => toggleSelect(b.id)} className="h-3.5 w-3.5 accent-sky-500" data-testid={`select-${b.id}`} />
                                        </td>
                                        <td className="py-2 px-2">
                                            <div className="font-mono font-semibold text-sky-600">#{b.id.slice(0, 6).toUpperCase()}</div>
                                            <div className="text-[10px] text-slate-500 leading-tight">
                                                {b.created_at ? (() => {
                                                    try {
                                                        const d = new Date(b.created_at);
                                                        return (
                                                            <>
                                                                {d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Istanbul" })}
                                                                <span className="ml-1 text-slate-400">{d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Istanbul" })}</span>
                                                            </>
                                                        );
                                                    } catch { return (b.created_at || "").slice(0, 10); }
                                                })() : "—"}
                                            </div>
                                        </td>
                                        <td className="py-2 px-2">
                                            <div className="font-semibold text-slate-900 dark:text-white truncate max-w-[180px]">{b.hotel_name}</div>
                                            <div className="text-[10px] text-slate-500">{b.room_type_name} · {b.destination_name}</div>
                                        </td>
                                        <td className="py-2 px-2">
                                            <div className="text-slate-900 dark:text-white">{b.guest.first_name} {b.guest.last_name}</div>
                                            <div className="text-[10px] text-slate-500">{b.guest.phone || b.guest.email}</div>
                                        </td>
                                        <td className="py-2 px-2 whitespace-nowrap">
                                            <div className="text-slate-900 dark:text-white text-[11px] font-medium">
                                                {formatDate(b.check_in)} <span className="text-slate-400">→</span> {formatDate(b.check_out)} <span className="text-[10px] text-slate-500">· {b.nights}n</span>
                                            </div>
                                        </td>
                                        <td className="py-2 px-2 text-center text-slate-700 dark:text-slate-300 whitespace-nowrap">
                                            {b.adults}y{b.children > 0 ? ` ${b.children}ç${(b.child_ages || []).length ? `(${(b.child_ages || []).join(",")})` : ""}` : ""}/{b.rooms}o
                                        </td>
                                        <td className="py-2 px-2 text-right whitespace-nowrap">
                                            <div className="font-bold text-slate-900 dark:text-white leading-tight">{formatTRY(b.total_amount)}</div>
                                            <div className="text-[10px] text-amber-700 dark:text-amber-400 font-semibold leading-tight">{formatTRY(b.cost_amount || 0)}</div>
                                            <div className="text-[10px] text-emerald-700 dark:text-emerald-400 font-bold leading-tight">{formatTRY(b.profit || 0)}</div>
                                        </td>
                                        <td className="py-2 px-2 text-right whitespace-nowrap">
                                            <div className="text-[11px] font-semibold text-emerald-600">{formatTRY(paid)}</div>
                                            <div className="text-[10px] text-rose-500">{formatTRY(remaining)}</div>
                                            <div className="w-full h-1 bg-slate-200 dark:bg-slate-800 rounded-full mt-0.5 overflow-hidden">
                                                <div className="h-full bg-emerald-500" style={{ width: `${paidPct}%` }}></div>
                                            </div>
                                        </td>
                                        <td className="py-2 px-2">
                                            {b.payment_method ? (
                                                <span data-testid={`pm-cell-${b.id}`} className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-violet-100 dark:bg-violet-500/10 text-violet-700 dark:text-violet-300">
                                                    {PM_LABEL[b.payment_method] || b.payment_method}
                                                </span>
                                            ) : (
                                                <span className="text-[10px] text-slate-400">—</span>
                                            )}
                                        </td>
                                        <td className="py-2 px-2">
                                            <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 dark:text-slate-200">
                                                <UserCheck className="h-3 w-3 text-violet-500" />
                                                {b.sales_agent_name || (b.source === "online" ? "Online" : "—")}
                                            </div>
                                        </td>
                                        <td className="py-2 px-2">
                                            <div className="flex flex-col gap-1">
                                                <StatusBadge value={b.status} type="booking" />
                                                {b.hotel_confirm_status === "confirmed" && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-emerald-500 text-white">✓ Otel</span>}
                                                {b.hotel_confirm_status === "rejected" && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-md bg-red-500 text-white">✗ Otel</span>}
                                            </div>
                                        </td>
                                        <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center justify-center gap-1">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setQuickTargetBooking(b); setVoucherModal(true); }}
                                                    data-testid={`row-voucher-${b.id}`}
                                                    title="Voucher oluştur"
                                                    className="w-7 h-7 rounded-md bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 transition-colors flex items-center justify-center font-black text-xs"
                                                >
                                                    V
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setQuickTargetBooking(b); setEmailModal(true); }}
                                                    data-testid={`row-email-${b.id}`}
                                                    title="Mail at (otel / misafir)"
                                                    className="p-1.5 rounded-md bg-sky-100 hover:bg-sky-200 dark:bg-sky-950/40 dark:hover:bg-sky-900/60 text-sky-700 dark:text-sky-300 transition-colors"
                                                >
                                                    <Mail className="h-3.5 w-3.5" />
                                                </button>
                                                <HotelConfirmButton booking={b} onChange={load} />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {/* Pagination */}
                {total > PAGE_SIZE && (
                    <div data-testid="bookings-pagination" className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 dark:border-slate-800 flex-wrap">
                        <div className="text-xs text-slate-500">
                            <span className="font-semibold">{((page - 1) * PAGE_SIZE) + 1}</span>
                            <span> – </span>
                            <span className="font-semibold">{Math.min(page * PAGE_SIZE, total)}</span>
                            <span> arası gösteriliyor</span>
                            <span className="mx-1">·</span>
                            <span className="font-semibold">{total}</span>
                            <span> toplam rezervasyon</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                data-testid="page-first"
                                onClick={() => setPage(1)}
                                disabled={page === 1}
                                className="px-2 py-1.5 rounded-lg text-xs font-bold disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800"
                            >« İlk</button>
                            <button
                                data-testid="page-prev"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800"
                            >‹ Önceki</button>
                            {(() => {
                                const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
                                const windowSize = 5;
                                const start = Math.max(1, Math.min(page - 2, totalPages - windowSize + 1));
                                const end = Math.min(totalPages, start + windowSize - 1);
                                const pages = [];
                                for (let i = start; i <= end; i++) pages.push(i);
                                return pages.map((p) => (
                                    <button
                                        key={p}
                                        data-testid={`page-${p}`}
                                        onClick={() => setPage(p)}
                                        className={`min-w-[32px] px-2 py-1.5 rounded-lg text-xs font-bold ${p === page ? "bg-sky-500 text-white" : "hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"}`}
                                    >{p}</button>
                                ));
                            })()}
                            <button
                                data-testid="page-next"
                                onClick={() => setPage((p) => Math.min(Math.ceil(total / PAGE_SIZE), p + 1))}
                                disabled={page >= Math.ceil(total / PAGE_SIZE)}
                                className="px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800"
                            >Sonraki ›</button>
                            <button
                                data-testid="page-last"
                                onClick={() => setPage(Math.ceil(total / PAGE_SIZE))}
                                disabled={page >= Math.ceil(total / PAGE_SIZE)}
                                className="px-2 py-1.5 rounded-lg text-xs font-bold disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800"
                            >Son »</button>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Drawer */}
            {detail && (
                <div className="fixed inset-0 z-40 flex" onClick={() => setDetail(null)}>
                    <div className="flex-1 bg-black/40" />
                    <div data-testid="booking-detail-drawer" className="w-full max-w-2xl bg-slate-50 dark:bg-slate-950 h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        {/* Top action bar (hidden when printing) */}
                        <div className="sticky top-0 z-10 px-6 py-3 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between print:hidden">
                            <div className="flex items-center gap-2">
                                <Receipt className="h-5 w-5 text-sky-500" />
                                <span className="font-display font-bold text-slate-900 dark:text-white">
                                    {editMode ? "Rezervasyon Düzenle" : voucherView ? "Voucher Önizleme" : "Rezervasyon Detayı"}
                                </span>
                            </div>
                            <div className="flex gap-1.5">
                                {!editMode && !voucherView && (
                                    <>
                                        <button data-testid="open-voucher-modal" onClick={() => setVoucherModal(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold"><Receipt className="h-3.5 w-3.5" /> Voucher Oluştur</button>
                                        {detail.status !== "rights_reserved" && detail.status !== "refund_pending" && detail.status !== "refunded" && (
                                            <>
                                                <button data-testid="action-rights-reserved" onClick={handleRightsReserved} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-100 hover:bg-violet-200 dark:bg-violet-900/40 dark:hover:bg-violet-900/60 text-violet-800 dark:text-violet-200 text-xs font-bold" title="İade yapılmaz, müşteri bakiyesi içeride kalır">⏸ Hakkı Saklı</button>
                                                <button data-testid="action-refund-request" onClick={handleRefundRequest} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-100 hover:bg-orange-200 dark:bg-orange-900/40 dark:hover:bg-orange-900/60 text-orange-800 dark:text-orange-200 text-xs font-bold" title="Finans modülüne iade talebi gönderir">↩ İade İste</button>
                                            </>
                                        )}
                                        <button data-testid="open-audit-log" onClick={() => setShowAuditLog(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold"><History className="h-3.5 w-3.5" /> Geçmiş</button>
                                        <button data-testid="edit-booking" onClick={startEdit} className="px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold">Düzenle</button>
                                    </>
                                )}
                                {voucherView && (
                                    <>
                                        <button onClick={() => window.print()} data-testid="print-voucher" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold"><Printer className="h-3.5 w-3.5" /> Yazdır</button>
                                        <button onClick={() => setVoucherView(false)} data-testid="close-voucher" className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold">Kapat</button>
                                    </>
                                )}
                                <button onClick={() => setDetail(null)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                            </div>
                        </div>

                        <div className="p-6">
                            {showAuditLog ? (
                                <AuditLogPanel logs={auditLogs} onClose={() => setShowAuditLog(false)} />
                            ) : editMode ? (
                                <BookingEditForm
                                    editForm={editForm}
                                    setEditForm={setEditForm}
                                    hotel={hotel}
                                    ratePlans={ratePlans}
                                    staffList={staffList}
                                    booking={detail}
                                    onOpenPayment={() => setPaymentModal(true)}
                                    onDeletePayment={deletePayment}
                                    onSave={saveEdit}
                                    onCancel={() => setEditMode(false)}
                                />
                            ) : voucherView ? (
                                <BookingVoucher booking={detail} hotel={hotel} opts={voucherOpts} />
                            ) : (
                                <BookingSummary
                                    booking={detail}
                                    hotel={hotel}
                                    onStatusChange={(payload) => updateStatus(detail.id, payload)}
                                    onOpenPayment={() => setPaymentModal(true)}
                                    onOpenEmail={() => setEmailModal(true)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Voucher options modal — supports both drawer (detail) and quick action (quickTargetBooking) */}
            {voucherModal && (detail || quickTargetBooking) && (
                <VoucherOptionsModal
                    opts={voucherOpts}
                    setOpts={setVoucherOpts}
                    onCancel={() => { setVoucherModal(false); setQuickTargetBooking(null); }}
                    onCreate={() => {
                        setVoucherModal(false);
                        // Quick-action path: send email or open PDF directly, without opening drawer view
                        if (quickTargetBooking && !detail) {
                            setDetail(quickTargetBooking);
                            setQuickTargetBooking(null);
                            setVoucherView(true);
                        } else {
                            setVoucherView(true);
                        }
                    }}
                />
            )}

            {/* Payment modal */}
            {paymentModal && detail && (
                <PaymentModal
                    booking={detail}
                    onCancel={() => setPaymentModal(false)}
                    onSubmit={addPayment}
                />
            )}

            {/* Email send modal — supports both drawer and quick action */}
            {emailModal && (detail || quickTargetBooking) && (
                <EmailSendModal
                    booking={detail || quickTargetBooking}
                    hotel={hotel}
                    onCancel={() => { setEmailModal(false); setQuickTargetBooking(null); }}
                    onSubmit={sendEmail}
                />
            )}
        </AdminLayout>
    );
}

/* ========== SUMMARY (default detail view, with quick actions) ========== */
function BookingSummary({ booking, hotel, onStatusChange, onOpenPayment, onOpenEmail }) {
    const b = booking;
    const pnr = b.id.slice(0, 8).toUpperCase();
    const payments = b.payments || [];
    const paid = payments.reduce((s, p) => s + (p.amount || 0), 0) || b.paid_amount || 0;
    const remaining = Math.max(0, (b.total_amount || 0) - paid);
    const methodLabels = { cash: "Nakit", bank_transfer: "Havale/EFT", card: "Kredi Kartı", stripe: "Stripe", other: "Diğer" };

    return (
        <div className="space-y-4">
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
                <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Otel</div>
                        <div className="font-display text-xl font-bold text-slate-900 dark:text-white">{b.hotel_name}</div>
                        <div className="flex items-center gap-1 text-sm text-slate-500 mt-0.5"><MapPin className="h-3.5 w-3.5" /> {b.destination_name}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">PNR</div>
                        <div className="font-mono text-lg font-black text-sky-600 dark:text-sky-400">{pnr}</div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                    <StatusBadge value={b.status} type="booking" size="md" />
                    <StatusBadge value={b.payment_status} type="payment" size="md" />
                    <span className="ml-auto text-xs text-slate-500">Oluşturulma: {b.created_at ? formatDate(b.created_at.slice(0, 10)) : "-"}</span>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <InfoBox label="Giriş" value={formatDate(b.check_in)} accent />
                <InfoBox label="Çıkış" value={formatDate(b.check_out)} accent />
                <InfoBox label="Gece / Oda" value={`${b.nights} gece · ${b.rooms} oda`} />
                <InfoBox label="Misafir" value={`${b.adults}y ${b.children}ç${(b.child_ages || []).length ? ` (${(b.child_ages || []).map((a) => `${a} yaş`).join(", ")})` : ""}`} />
                <InfoBox label="Oda Tipi" value={b.room_type_name} />
                <InfoBox label="Pansiyon" value={b.board || b.rate_plan_name || "—"} />
                <InfoBox label="Toplam Tutar" value={formatTRY(b.total_amount)} />
            </div>

            {(b.discount_amount || 0) > 0 && Array.isArray(b.discounts_applied) && b.discounts_applied.length > 0 && (
                <div data-testid="booking-discounts-info" className="p-3 rounded-xl bg-fuchsia-50 dark:bg-fuchsia-950/30 border border-fuchsia-200 dark:border-fuchsia-900 space-y-2">
                    <div className="flex items-center gap-2">
                        <span className="text-base">🎟️</span>
                        <span className="text-xs font-bold uppercase tracking-widest text-fuchsia-700 dark:text-fuchsia-300">Uygulanan İndirimler</span>
                        <span className="ml-auto text-sm font-bold text-fuchsia-700 dark:text-fuchsia-300">-{formatTRY(b.discount_amount)}</span>
                    </div>
                    <div className="text-[11px] text-slate-500">
                        Asıl tutar: <span className="line-through font-medium text-slate-700 dark:text-slate-200">{formatTRY(b.original_subtotal || b.total_amount)}</span>
                        {(b.cost_discount_amount || 0) > 0 && (
                            <> · Maliyetten indirim: <span className="font-medium text-amber-700 dark:text-amber-300">-{formatTRY(b.cost_discount_amount)}</span></>
                        )}
                    </div>
                    <div className="space-y-1">
                        {b.discounts_applied.map((d) => (
                            <div key={d.id} className="flex items-center justify-between text-xs bg-white dark:bg-slate-900 rounded-lg px-2.5 py-1.5">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-[9px] font-mono text-slate-400">{d.code}</span>
                                    <span className="font-medium text-slate-900 dark:text-white truncate">{d.name}</span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-100 dark:bg-fuchsia-950/60 text-fuchsia-700 dark:text-fuchsia-300 font-bold">
                                        {d.discount_type === "percentage" ? `%${d.discount_value}` : formatTRY(d.discount_value)}
                                    </span>
                                    <span className="font-bold text-fuchsia-600 dark:text-fuchsia-400">-{formatTRY(d.amount)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {b.insurance && (
                <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    <span className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">Ücretsiz İptal Sigortası Aktif</span>
                    <span className="ml-auto text-sm font-bold text-emerald-700 dark:text-emerald-300">{formatTRY(b.insurance_amount || 0)}</span>
                </div>
            )}

            {b.hotel_confirm_status === "confirmed" && (
                <div data-testid="hotel-confirmed-banner" className="p-3 rounded-xl bg-emerald-500 text-white flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-white" />
                    <span className="text-sm font-bold">✓ Otel Konfirmasyonu Aldı</span>
                    {b.hotel_confirm_at && <span className="ml-auto text-xs opacity-90">{formatDate(b.hotel_confirm_at.slice(0, 10))}</span>}
                </div>
            )}
            {b.hotel_confirm_status === "rejected" && (
                <div data-testid="hotel-rejected-banner" className="p-3 rounded-xl bg-red-500 text-white">
                    <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-white" />
                        <span className="text-sm font-bold">✗ Otel Reddetti</span>
                        {b.hotel_confirm_at && <span className="ml-auto text-xs opacity-90">{formatDate(b.hotel_confirm_at.slice(0, 10))}</span>}
                    </div>
                    {b.hotel_reject_reason && <div className="mt-1.5 text-xs opacity-90 italic">Sebep: {b.hotel_reject_reason}</div>}
                </div>
            )}

            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Misafir</div>
                <div className="font-semibold text-slate-900 dark:text-white">{b.guest.first_name} {b.guest.last_name}</div>
                <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-1"><Mail className="h-3.5 w-3.5" /> {b.guest.email}</div>
                {b.guest.phone && <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5"><Phone className="h-3.5 w-3.5" /> {b.guest.phone}</div>}
            </div>

            {(b.special_requests || b.admin_note) && (
                <div className="space-y-2">
                    {b.special_requests && (
                        <div className="p-3 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-100 dark:border-sky-900">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700 dark:text-sky-300 mb-1">Misafir İsteği</div>
                            <div className="text-sm text-slate-800 dark:text-slate-200">{b.special_requests}</div>
                        </div>
                    )}
                    {b.admin_note && (
                        <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-300 mb-1">Admin Notu</div>
                            <div className="text-sm text-slate-800 dark:text-slate-200">{b.admin_note}</div>
                        </div>
                    )}
                </div>
            )}

            {/* Email actions */}
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Send className="h-4 w-4 text-sky-500" />
                        <span className="font-display text-sm font-bold uppercase tracking-widest text-slate-700 dark:text-slate-200">E-posta Gönder</span>
                    </div>
                    <button data-testid="open-email-modal" onClick={onOpenEmail} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold">
                        <Mail className="h-3.5 w-3.5" /> Voucher Mail At
                    </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">Misafire ve/veya otele rezervasyon voucher'ını e-posta ile gönder.</p>
            </div>
        </div>
    );
}

/* ========== VOUCHER OPTIONS MODAL ========== */
function VoucherOptionsModal({ opts, setOpts, onCancel, onCreate }) {
    const toggle = (key) => setOpts((o) => ({ ...o, [key]: !o[key] }));

    const OPTIONS = [
        { key: "showPrice", label: "Fiyatları göster", desc: "Toplam tutar ve gece × fiyat detayı" },
        { key: "showPayment", label: "Ödeme bilgilerini göster", desc: "Ödenen / Kalan tutar" },
        { key: "showHotelContact", label: "Otel iletişim bilgileri", desc: "Adres, telefon, bölge" },
        { key: "showGuestPhone", label: "Misafir telefonu", desc: "Misafir telefon numarasını göster" },
        { key: "showSpecialRequests", label: "Misafir isteğini göster", desc: "Özel istekler bölümü" },
        { key: "showAdminNote", label: "Admin notunu göster", desc: "Dahili not (genelde gizli tutulur)" },
        { key: "showStripeRef", label: "Stripe referansını göster", desc: "Teknik ödeme referans no" },
    ];

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onCancel}>
            <div className="w-full max-w-xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                    <div>
                        <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">Voucher Oluştur</h2>
                        <p className="text-xs text-slate-500 mt-0.5">Voucher'da gösterilecek bilgileri seçin.</p>
                    </div>
                    <button onClick={onCancel} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                </div>

                <div className="p-6 space-y-2 max-h-[60vh] overflow-y-auto">
                    {/* Preset buttons */}
                    <div className="flex flex-wrap gap-2 mb-4">
                        <button
                            onClick={() => setOpts({ showPrice: true, showPayment: true, showAdminNote: false, showHotelContact: true, showStripeRef: false, showGuestPhone: true, showSpecialRequests: true })}
                            data-testid="preset-full"
                            className="px-3 py-1.5 rounded-lg bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800 text-sky-700 dark:text-sky-300 text-xs font-semibold"
                        >✓ Tam Voucher (fiyatlı)</button>
                        <button
                            onClick={() => setOpts({ showPrice: false, showPayment: false, showAdminNote: false, showHotelContact: true, showStripeRef: false, showGuestPhone: true, showSpecialRequests: true })}
                            data-testid="preset-no-price"
                            className="px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold"
                        >Fiyatsız (otel için)</button>
                        <button
                            onClick={() => setOpts({ showPrice: true, showPayment: true, showAdminNote: true, showHotelContact: true, showStripeRef: true, showGuestPhone: true, showSpecialRequests: true })}
                            data-testid="preset-internal"
                            className="px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-xs font-semibold"
                        >İç kullanım (her şey)</button>
                    </div>

                    {OPTIONS.map((o) => (
                        <label key={o.key} data-testid={`opt-${o.key}`} className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${opts[o.key] ? "bg-sky-50 dark:bg-sky-950/30 border-sky-300 dark:border-sky-700" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300"}`}>
                            <input type="checkbox" checked={opts[o.key]} onChange={() => toggle(o.key)} className="mt-0.5 h-5 w-5 accent-sky-500" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    {opts[o.key] ? <Eye className="h-3.5 w-3.5 text-sky-500" /> : <EyeOff className="h-3.5 w-3.5 text-slate-400" />}
                                    <span className={`text-sm font-bold ${opts[o.key] ? "text-sky-900 dark:text-sky-100" : "text-slate-900 dark:text-white"}`}>{o.label}</span>
                                </div>
                                <div className="text-xs text-slate-500 mt-0.5 ml-5">{o.desc}</div>
                            </div>
                        </label>
                    ))}
                </div>

                <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                    <button onClick={onCancel} className="px-5 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-semibold text-sm">İptal</button>
                    <button data-testid="create-voucher-btn" onClick={onCreate} className="px-6 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm inline-flex items-center gap-2">
                        <Receipt className="h-4 w-4" /> Voucher'ı Görüntüle
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ========== VOUCHER (read-only, professional, printable, configurable) ========== */
function BookingVoucher({ booking, hotel, opts = {} }) {
    const b = booking;
    const site = useSiteContent();
    const brand = site?.brand || {};
    const brandName = brand.name || "Msonline Travel";
    const pnr = b.id.slice(0, 8).toUpperCase();
    const paid = b.paid_amount != null ? b.paid_amount : (b.payment_status === "paid" ? b.total_amount : 0);
    const remaining = Math.max(0, (b.total_amount || 0) - (paid || 0));
    const perNight = b.nights > 0 ? (b.total_amount / b.nights) : 0;

    const o = {
        showPrice: true,
        showPayment: true,
        showAdminNote: false,
        showHotelContact: true,
        showStripeRef: false,
        showGuestPhone: true,
        showSpecialRequests: true,
        ...opts,
    };

    return (
        <div className="space-y-4" data-testid="booking-voucher">
            {/* Header card */}
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="bg-gradient-to-br from-sky-500 to-indigo-600 px-6 py-5 text-white">
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-widest opacity-80">Rezervasyon Voucher</div>
                                <div className="font-display text-2xl font-bold mt-1">{b.hotel_name}</div>
                                <div className="flex items-center gap-1 text-sm opacity-90 mt-1"><MapPin className="h-3.5 w-3.5" /> {b.destination_name}</div>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] font-bold uppercase tracking-widest opacity-80">PNR</div>
                            <div className="font-mono text-2xl font-black tracking-wider" data-testid="voucher-pnr">{pnr}</div>
                        </div>
                    </div>
                </div>
                <div className="px-6 py-3 flex flex-wrap items-center gap-2 border-t border-slate-200 dark:border-slate-800">
                    <StatusBadge value={b.status} type="booking" size="md" />
                    <StatusBadge value={b.payment_status} type="payment" size="md" />
                    <span className="ml-auto text-xs text-slate-500">Oluşturulma: {b.created_at ? formatDate(b.created_at.slice(0, 10)) : "-"}</span>
                </div>
            </div>

            {/* Konaklama Özeti */}
            <Section icon={<Calendar className="h-4 w-4" />} title="Konaklama Özeti">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <InfoBox label="Giriş" value={formatDate(b.check_in)} accent />
                    <InfoBox label="Çıkış" value={formatDate(b.check_out)} accent />
                    <InfoBox label="Gece" value={`${b.nights} gece`} />
                    <InfoBox label="Oda Tipi" value={b.room_type_name} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                    <InfoBox label="Pansiyon" value={b.board || b.rate_plan_name || "—"} accent />
                    <InfoBox label="Yetişkin" value={`${b.adults}`} />
                    <InfoBox label="Çocuk" value={`${b.children}${(b.child_ages || []).length ? ` (${(b.child_ages || []).map((a) => `${a} yaş`).join(", ")})` : ""}`} />
                    <InfoBox label="Oda Sayısı" value={`${b.rooms}`} />
                </div>
            </Section>

            {/* Misafir Bilgileri */}
            <Section icon={<User className="h-4 w-4" />} title="Misafir Bilgileri">
                <div className="grid grid-cols-2 gap-3">
                    <InfoBox label="Ad Soyad" value={`${b.guest.first_name} ${b.guest.last_name}`} />
                    <InfoBox label="E-posta" value={b.guest.email} icon={<Mail className="h-3 w-3" />} />
                    {o.showGuestPhone && <InfoBox label="Telefon" value={b.guest.phone || "-"} icon={<Phone className="h-3 w-3" />} />}
                    <InfoBox label="Misafir Sayısı" value={`${(b.adults || 0) + (b.children || 0)} kişi`} />
                </div>
            </Section>

            {/* Otel İletişim */}
            {o.showHotelContact && hotel && (
                <Section icon={<Hotel className="h-4 w-4" />} title="Otel İletişim">
                    <div className="grid grid-cols-2 gap-3">
                        <InfoBox label="Otel" value={hotel.name} />
                        <InfoBox label="Bölge" value={hotel.region || b.destination_name} />
                        {hotel.phone && <InfoBox label="Telefon" value={hotel.phone} icon={<Phone className="h-3 w-3" />} />}
                        {hotel.address && <InfoBox label="Adres" value={hotel.address} />}
                    </div>
                </Section>
            )}

            {/* Fiyat Dökümü */}
            {o.showPrice && (
                <Section icon={<CreditCard className="h-4 w-4" />} title="Fiyat & Ödeme">
                    <div className="rounded-xl bg-slate-50 dark:bg-slate-800/40 p-4 space-y-2 text-sm">
                        <div className="flex justify-between text-slate-600 dark:text-slate-300">
                            <span>{b.nights} gece × {formatTRY(perNight)}</span>
                            <span className="font-medium">{formatTRY(b.subtotal || b.total_amount)}</span>
                        </div>
                        <div className="flex justify-between text-slate-600 dark:text-slate-300">
                            <span>{b.rooms} oda × {b.room_type_name}</span>
                            <span className="font-medium text-slate-400">dahil</span>
                        </div>
                        {b.insurance && (b.insurance_amount || 0) > 0 && (
                            <div className="flex justify-between text-emerald-700 dark:text-emerald-300">
                                <span>Ücretsiz İptal Sigortası (%1)</span>
                                <span className="font-medium">+{formatTRY(b.insurance_amount)}</span>
                            </div>
                        )}
                        <div className="border-t border-slate-200 dark:border-slate-700 pt-2 flex justify-between text-base font-bold text-slate-900 dark:text-white">
                            <span>Toplam Tutar</span>
                            <span data-testid="voucher-total">{formatTRY(b.total_amount)}</span>
                        </div>
                        {o.showPayment && (
                            <>
                                <div className="flex justify-between text-sm text-emerald-600 dark:text-emerald-400 font-semibold">
                                    <span>Ödenen</span>
                                    <span data-testid="voucher-paid">{formatTRY(paid)}</span>
                                </div>
                                {remaining > 0 && (
                                    <div className="flex justify-between text-sm text-amber-600 dark:text-amber-400 font-semibold">
                                        <span>Kalan</span>
                                        <span data-testid="voucher-remaining">{formatTRY(remaining)}</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    {o.showStripeRef && b.stripe_session_id && (
                        <div className="mt-3 text-[10px] text-slate-400 font-mono break-all">
                            Stripe ref: {b.stripe_session_id}
                        </div>
                    )}
                </Section>
            )}

            {/* Notlar */}
            {((o.showSpecialRequests && b.special_requests) || (o.showAdminNote && b.admin_note)) && (
                <Section icon={<FileText className="h-4 w-4" />} title="Notlar">
                    {o.showSpecialRequests && b.special_requests && (
                        <div className="mb-2 p-3 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-100 dark:border-sky-900">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-sky-700 dark:text-sky-300 mb-1">Misafir İsteği</div>
                            <div className="text-sm text-slate-800 dark:text-slate-200">{b.special_requests}</div>
                        </div>
                    )}
                    {o.showAdminNote && b.admin_note && (
                        <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-300 mb-1">Admin Notu</div>
                            <div className="text-sm text-slate-800 dark:text-slate-200">{b.admin_note}</div>
                        </div>
                    )}
                </Section>
            )}

            {/* Footer (visible on print) */}
            <div className="hidden print:block text-center text-xs text-slate-500 pt-6 border-t border-slate-200 mt-4">
                Bu voucher rezervasyonunuzun resmi onayıdır.
            </div>
        </div>
    );
}

/* ========== AUDIT LOG PANEL ========== */
function AuditLogPanel({ logs, onClose }) {
    const fmtVal = (v) => {
        if (v === null || v === undefined || v === "") return "boş";
        if (typeof v === "number") {
            // money-like fields formatted in TRY when reasonable
            return v.toLocaleString("tr-TR", { maximumFractionDigits: 2 });
        }
        if (typeof v === "object") return JSON.stringify(v);
        return String(v);
    };
    const fmtDate = (iso) => {
        try { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
    };
    // Render a single field change as a human sentence
    const renderChange = (c, userName) => {
        const label = c.label || c.field;
        const oldV = fmtVal(c.old);
        const newV = fmtVal(c.new);
        // Money-like detection
        const isMoney = /Tutar|Toplam|Ödenen|Maliyet|Komisyon|Sigorta|Ara/i.test(label);
        if (oldV === "boş") {
            return <span><b>{userName}</b>, <b>{label}</b> alanını <span className="text-emerald-700 font-semibold">{newV}{isMoney ? " ₺" : ""}</span> olarak ekledi</span>;
        }
        if (newV === "boş") {
            return <span><b>{userName}</b>, <b>{label}</b> alanını (<span className="text-red-600 line-through">{oldV}{isMoney ? " ₺" : ""}</span>) temizledi</span>;
        }
        return <span><b>{userName}</b>, <b>{label}</b> alanını <span className="text-red-600 line-through">{oldV}{isMoney ? " ₺" : ""}</span> yerine <span className="text-emerald-700 font-semibold">{newV}{isMoney ? " ₺" : ""}</span> yaptı</span>;
    };

    const sourceBadge = (s) => {
        const map = {
            create: { bg: "bg-emerald-100 text-emerald-700", label: "Oluşturuldu", icon: "+" },
            edit: { bg: "bg-sky-100 text-sky-700", label: "Düzenleme", icon: "✎" },
            payment: { bg: "bg-amber-100 text-amber-700", label: "Ödeme", icon: "₺" },
            status: { bg: "bg-violet-100 text-violet-700", label: "Durum", icon: "•" },
            note: { bg: "bg-slate-100 text-slate-700", label: "Not", icon: "·" },
        };
        const m = map[s] || { bg: "bg-slate-100 text-slate-700", label: s, icon: "·" };
        return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${m.bg}`}><span className="text-[11px]">{m.icon}</span>{m.label}</span>;
    };
    return (
        <div data-testid="audit-log-panel" className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="font-display text-base font-bold text-slate-900 dark:text-white">İşlem Geçmişi</h3>
                    <p className="text-xs text-slate-500 flex items-center gap-1.5">
                        <span className="inline-block px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 text-[9px] font-bold uppercase">🔒 Sadece okunur</span>
                        Bu kayıtlar değiştirilemez veya silinemez (kanuni gereklilik)
                    </p>
                </div>
                <button onClick={onClose} className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-semibold">Kapat</button>
            </div>
            {logs.length === 0 ? (
                <div className="py-12 text-center text-slate-500 border border-dashed border-slate-300 dark:border-slate-700 rounded-xl">
                    Henüz kayıt yok.
                </div>
            ) : (
                <div className="space-y-3">
                    {logs.map((l) => (
                        <div key={l.id} className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60">
                            <div className="flex items-center justify-between mb-2">
                                {sourceBadge(l.source)}
                                <span className="text-xs text-slate-500">{fmtDate(l.created_at)}</span>
                            </div>
                            {l.note && (
                                <div className="text-sm text-slate-700 dark:text-slate-200 mb-2">
                                    {l.note}
                                </div>
                            )}
                            {(l.changes || []).length > 0 && (
                                <ul className="space-y-1.5 list-disc list-inside marker:text-slate-300">
                                    {l.changes.map((c, idx) => (
                                        <li key={idx} className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                                            {renderChange(c, l.user_name || "Sistem")}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ========== EDIT FORM (grouped sections) ========== */
function BookingEditForm({ editForm, setEditForm, hotel, ratePlans = [], staffList = [], booking, onOpenPayment, onDeletePayment, onSave, onCancel }) {
    if (!editForm) return null;
    const onF = (k) => (e) => setEditForm({ ...editForm, [k]: e.target.value });
    const onN = (k) => (e) => setEditForm({ ...editForm, [k]: e.target.value === "" ? null : Number(e.target.value) });
    const payments = booking?.payments || [];
    const paid = payments.reduce((s, p) => s + (p.amount || 0), 0) || (booking?.paid_amount || 0);
    const total = booking?.total_amount || editForm.total_amount || 0;
    const remaining = Math.max(0, total - paid);
    const methodLabels = { cash: "Nakit", bank_transfer: "Havale/EFT", card: "Kredi Kartı", stripe: "Stripe", other: "Diğer" };
    return (
        <div className="space-y-5">
            <FormSection icon={<Calendar className="h-4 w-4" />} title="Tarih & Oda">
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Giriş Tarihi"><input type="date" value={editForm.check_in} onChange={onF("check_in")} className={INPUT} /></Field>
                    <Field label="Çıkış Tarihi"><input type="date" value={editForm.check_out} onChange={onF("check_out")} className={INPUT} /></Field>
                    <Field label="Yetişkin"><input type="number" min="1" value={editForm.adults} onChange={(e) => setEditForm({ ...editForm, adults: Number(e.target.value) })} className={INPUT} /></Field>
                    <Field label="Çocuk Sayısı"><input type="number" min="0" value={editForm.children} onChange={(e) => {
                        const n = Number(e.target.value);
                        // Resize child_ages array
                        const arr = [...(editForm.child_ages || [])];
                        while (arr.length < n) arr.push(7);
                        arr.length = n;
                        setEditForm({ ...editForm, children: n, child_ages: arr });
                    }} className={INPUT} /></Field>
                    {(editForm.children || 0) > 0 && (
                        <Field label={`Çocuk Yaşları (${(editForm.child_ages || []).length})`}>
                            <div className="flex flex-wrap gap-2">
                                {(editForm.child_ages || []).map((age, idx) => (
                                    <div key={idx} className="flex items-center gap-1">
                                        <span className="text-[10px] text-slate-500 font-bold">#{idx + 1}</span>
                                        <input type="number" min="0" max="17" value={age} onChange={(e) => {
                                            const arr = [...(editForm.child_ages || [])];
                                            arr[idx] = Number(e.target.value);
                                            setEditForm({ ...editForm, child_ages: arr });
                                        }} className="w-16 px-2 py-1 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs" />
                                        <span className="text-[10px] text-slate-500">yaş</span>
                                    </div>
                                ))}
                            </div>
                        </Field>
                    )}
                    <Field label="Oda Sayısı"><input type="number" min="1" value={editForm.rooms} onChange={(e) => setEditForm({ ...editForm, rooms: Number(e.target.value) })} className={INPUT} /></Field>
                    <Field label="Oda Tipi">
                        <select value={editForm.room_type_id} onChange={onF("room_type_id")} className={INPUT}>
                            {(hotel?.room_types || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </Field>
                    <Field label="Fiyat Planı (Pansiyon)">
                        <select value={editForm.rate_plan_id || ""} onChange={onF("rate_plan_id")} className={INPUT}>
                            <option value="">— Seçim yok —</option>
                            {ratePlans
                                .filter(p => !editForm.room_type_id || (p.room_type_ids || []).includes(editForm.room_type_id) || p.room_type_id === editForm.room_type_id)
                                .map((p) => <option key={p.id} value={p.id}>{p.name} · {p.board}</option>)}
                        </select>
                    </Field>
                </div>
            </FormSection>

            <FormSection icon={<User className="h-4 w-4" />} title="Misafir Bilgileri">
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Ad"><input value={editForm.guest_first_name} onChange={onF("guest_first_name")} className={INPUT} /></Field>
                    <Field label="Soyad"><input value={editForm.guest_last_name} onChange={onF("guest_last_name")} className={INPUT} /></Field>
                    <Field label="E-posta"><input type="email" value={editForm.guest_email} onChange={onF("guest_email")} className={INPUT} /></Field>
                    <Field label="Telefon"><input value={editForm.guest_phone} onChange={onF("guest_phone")} className={INPUT} /></Field>
                </div>
            </FormSection>

            <FormSection icon={<CreditCard className="h-4 w-4" />} title="Fiyat & Ödeme">
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Ara Toplam (₺)"><input type="number" min="0" step="0.01" value={editForm.subtotal ?? ""} onChange={onN("subtotal")} className={INPUT} /></Field>
                    <Field label="Sigorta (₺)"><input type="number" min="0" step="0.01" value={editForm.insurance_amount ?? ""} onChange={onN("insurance_amount")} className={INPUT} /></Field>
                    <Field label="Maliyet (₺)"><input type="number" min="0" step="0.01" value={editForm.cost_amount ?? ""} onChange={onN("cost_amount")} className={INPUT} /></Field>
                    <Field label="Komisyon/Kâr (₺)"><input type="number" min="0" step="0.01" value={editForm.commission_amount ?? ""} onChange={onN("commission_amount")} className={INPUT} /></Field>
                    <Field label="Toplam Tutar (₺)"><input type="number" min="0" value={editForm.total_amount} onChange={(e) => setEditForm({ ...editForm, total_amount: Number(e.target.value) })} className={INPUT} /></Field>
                    <Field label="Ödenen Tutar (₺) — otomatik">
                        <div className="relative">
                            <input
                                type="number"
                                value={editForm.paid_amount ?? 0}
                                disabled
                                readOnly
                                data-testid="edit-paid-amount"
                                className={`${INPUT} bg-slate-100 dark:bg-slate-800/50 cursor-not-allowed text-slate-500 dark:text-slate-400`}
                                title="Manuel değiştirilemez — sadece 'Ödeme Ekle' / 'Ödeme Sil' ile güncellenir"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-slate-400 pointer-events-none">🔒 kilitli</span>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1">Bu alan sadece <b>Ödeme Ekle / Sil</b> ile otomatik güncellenir.</p>
                    </Field>
                    <Field label="Rezervasyon Durumu">
                        <select value={editForm.status} onChange={onF("status")} className={INPUT}>
                            <option value="pending">Beklemede</option>
                            <option value="confirmed">Onaylandı</option>
                            <option value="cancelled">İptal</option>
                            <option value="completed">Tamamlandı</option>
                            <option value="rights_reserved">Hakkı Saklı</option>
                            <option value="refund_pending">İade Bekliyor</option>
                            <option value="refunded">İade Edildi</option>
                        </select>
                    </Field>
                    <Field label="Ödeme Durumu">
                        <select value={editForm.payment_status} onChange={onF("payment_status")} className={INPUT}>
                            <option value="initiated">Bekliyor</option>
                            <option value="paid">Ödendi</option>
                            <option value="partial">Parçalı</option>
                            <option value="awaiting_transfer">Havale Bekliyor</option>
                            <option value="at_hotel">Otelde Ödenecek</option>
                            <option value="failed">Başarısız</option>
                            <option value="refunded">İade</option>
                        </select>
                    </Field>
                    <Field label="Ödeme Yöntemi">
                        <select value={editForm.payment_method || ""} onChange={onF("payment_method")} className={INPUT}>
                            <option value="">—</option>
                            <option value="stripe">Stripe</option>
                            <option value="bank_transfer">Havale/EFT</option>
                            <option value="at_hotel">Otelde Ödeme</option>
                            <option value="cash">Nakit</option>
                            <option value="card">Kart (Manuel)</option>
                        </select>
                    </Field>
                    <Field label="Kaynak">
                        <select value={editForm.source || "online"} onChange={onF("source")} className={INPUT}>
                            <option value="online">Online (Web)</option>
                            <option value="manual">Manuel</option>
                            <option value="phone">Telefon</option>
                            <option value="agency">Acente</option>
                            <option value="walk_in">Walk-in</option>
                        </select>
                    </Field>
                    <Field label="Satışı Yapan">
                        <select
                            data-testid="edit-sales-agent-select"
                            value={editForm.sales_agent_id || ""}
                            onChange={(e) => {
                                const id = e.target.value;
                                const u = staffList.find((s) => s.id === id);
                                setEditForm((f) => ({ ...f, sales_agent_id: id, sales_agent_name: u ? (u.full_name || u.email) : "" }));
                            }}
                            className={INPUT}
                        >
                            <option value="">— Seçim yok (Online) —</option>
                            {staffList.map((u) => (
                                <option key={u.id} value={u.id}>{u.full_name || u.email}{u.role ? ` · ${u.role}` : ""}</option>
                            ))}
                        </select>
                    </Field>
                </div>
            </FormSection>

            {booking && (
                <FormSection icon={<CreditCard className="h-4 w-4" />} title="Ödemeler">
                    <div className="grid grid-cols-3 gap-3 mb-3">
                        <div className="rounded-xl bg-slate-50 dark:bg-slate-800/40 p-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Toplam</div>
                            <div className="text-base font-bold text-slate-900 dark:text-white">{formatTRY(total)}</div>
                        </div>
                        <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 p-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Ödenen</div>
                            <div className="text-base font-bold text-emerald-700 dark:text-emerald-300">{formatTRY(paid)}</div>
                        </div>
                        <div className={`rounded-xl p-3 ${remaining > 0 ? "bg-amber-50 dark:bg-amber-950/30" : "bg-slate-50 dark:bg-slate-800/40"}`}>
                            <div className={`text-[10px] font-bold uppercase tracking-widest ${remaining > 0 ? "text-amber-700" : "text-slate-500"}`}>Kalan</div>
                            <div className={`text-base font-bold ${remaining > 0 ? "text-amber-700 dark:text-amber-300" : "text-slate-900 dark:text-white"}`}>{formatTRY(remaining)}</div>
                        </div>
                    </div>
                    <button type="button" data-testid="open-payment-modal" onClick={onOpenPayment} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold mb-3">
                        <Plus className="h-3.5 w-3.5" /> Ödeme Ekle
                    </button>
                    {payments.length === 0 ? (
                        <p className="text-sm text-slate-500 italic text-center py-3">Henüz ödeme kaydı yok.</p>
                    ) : (
                        <div className="space-y-1.5">
                            {payments.map((p) => (
                                <div key={p.id} data-testid={`payment-${p.id}`} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800">
                                    <div className="text-emerald-500"><CreditCard className="h-4 w-4" /></div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-bold text-slate-900 dark:text-white">{formatTRY(p.amount)} <span className="ml-1 text-[10px] font-semibold text-slate-500">· {methodLabels[p.method] || p.method}</span></div>
                                        <div className="text-[11px] text-slate-500">{p.date} {p.note ? `· ${p.note}` : ""}</div>
                                    </div>
                                    {onDeletePayment && (
                                        <button
                                            type="button"
                                            data-testid={`delete-payment-${p.id}`}
                                            onClick={() => onDeletePayment(p.id)}
                                            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-slate-400 hover:text-red-600 transition-colors"
                                            title="Ödemeyi sil"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </FormSection>
            )}

            <FormSection icon={<FileText className="h-4 w-4" />} title="Notlar">
                <Field label="Misafir İsteği"><textarea rows={2} value={editForm.special_requests} onChange={onF("special_requests")} className={INPUT} /></Field>
                <div className="mt-3" />
                <Field label="Admin Notu (sadece iç kullanım)"><textarea rows={2} value={editForm.admin_note} onChange={onF("admin_note")} className={INPUT} /></Field>
            </FormSection>

            <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex gap-2">
                <button onClick={onCancel} className="px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-semibold">Vazgeç</button>
                <button data-testid="save-booking-edit" onClick={onSave} className="flex-1 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-semibold">Değişiklikleri Kaydet</button>
            </div>
        </div>
    );
}

/* ========== HELPER COMPONENTS ========== */
function Section({ icon, title, children }) {
    return (
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
            <div className="flex items-center gap-2 mb-3">
                <span className="text-sky-500">{icon}</span>
                <h3 className="font-display text-sm font-bold uppercase tracking-widest text-slate-700 dark:text-slate-200">{title}</h3>
            </div>
            {children}
        </div>
    );
}

function FormSection({ icon, title, children }) {
    return (
        <div>
            <div className="flex items-center gap-2 mb-3">
                <span className="text-sky-500">{icon}</span>
                <h3 className="font-display text-sm font-bold uppercase tracking-widest text-slate-700 dark:text-slate-200">{title}</h3>
            </div>
            {children}
        </div>
    );
}

function InfoBox({ label, value, accent, icon }) {
    return (
        <div className={`rounded-xl p-3 ${accent ? "bg-sky-50 dark:bg-sky-950/30 border border-sky-100 dark:border-sky-900" : "bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800"}`}>
            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{icon}{label}</div>
            <div className={`text-sm font-semibold break-words ${accent ? "text-sky-900 dark:text-sky-100" : "text-slate-900 dark:text-white"}`}>{value || "-"}</div>
        </div>
    );
}

const INPUT = "w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-white";

function Field({ label, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>
            {children}
        </label>
    );
}

function StatusBadge({ value, type, size = "sm" }) {
    const map = type === "booking" ? {
        pending: { c: "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300", l: "Beklemede" },
        confirmed: { c: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300", l: "Onaylandı" },
        cancelled: { c: "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300", l: "İptal" },
        completed: { c: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200", l: "Tamamlandı" },
        rights_reserved: { c: "bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300", l: "Hakkı Saklı" },
        refund_pending: { c: "bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300", l: "İade Bekliyor" },
        refunded: { c: "bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300", l: "İade Edildi" },
    } : {
        initiated: { c: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200", l: "Ödeme Bekliyor" },
        paid: { c: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300", l: "Ödendi" },
        partial: { c: "bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300", l: "Parçalı Ödeme" },
        failed: { c: "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300", l: "Başarısız" },
        refunded: { c: "bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300", l: "İade" },
    };
    const b = map[value] || { c: "bg-slate-100 text-slate-700", l: value };
    const sz = size === "md" ? "text-xs px-2.5 py-1" : "text-[10px] px-2 py-0.5";
    return <span className={`font-bold rounded-md inline-block ${sz} ${b.c}`}>{b.l}</span>;
}


/* ========== PAYMENT MODAL ========== */
function PaymentModal({ booking, onCancel, onSubmit }) {
    const paid = (booking.payments || []).reduce((s, p) => s + (p.amount || 0), 0) || (booking.paid_amount || 0);
    const remaining = Math.max(0, (booking.total_amount || 0) - paid);
    const [amount, setAmount] = useState(remaining > 0 ? remaining : 0);
    const [method, setMethod] = useState("bank_transfer");
    const [note, setNote] = useState("");
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

    const METHODS = [
        { v: "cash", l: "Nakit" },
        { v: "bank_transfer", l: "Havale/EFT" },
        { v: "card", l: "Kredi Kartı (POS)" },
        { v: "stripe", l: "Stripe" },
        { v: "other", l: "Diğer" },
    ];

    const submit = () => {
        if (!amount || amount <= 0) return;
        onSubmit({ amount: Number(amount), method, note, date });
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onCancel}>
            <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                    <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">Ödeme Ekle</h2>
                    <button onClick={onCancel} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2">
                            <div className="text-[10px] uppercase font-bold text-slate-500">Toplam</div>
                            <div className="font-bold text-slate-900 dark:text-white">{formatTRY(booking.total_amount)}</div>
                        </div>
                        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/30 p-2">
                            <div className="text-[10px] uppercase font-bold text-emerald-700">Ödenen</div>
                            <div className="font-bold text-emerald-700 dark:text-emerald-300">{formatTRY(paid)}</div>
                        </div>
                        <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 p-2">
                            <div className="text-[10px] uppercase font-bold text-amber-700">Kalan</div>
                            <div className="font-bold text-amber-700 dark:text-amber-300">{formatTRY(remaining)}</div>
                        </div>
                    </div>

                    <Field label="Tutar (₺)">
                        <input data-testid="payment-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={INPUT} autoFocus />
                    </Field>
                    <Field label="Yöntem">
                        <select data-testid="payment-method" value={method} onChange={(e) => setMethod(e.target.value)} className={INPUT}>
                            {METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                        </select>
                    </Field>
                    <Field label="Tarih">
                        <input data-testid="payment-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={INPUT} />
                    </Field>
                    <Field label="Not (opsiyonel)">
                        <input data-testid="payment-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Örn: Kapora, dekont no, vb." className={INPUT} />
                    </Field>
                </div>
                <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                    <button onClick={onCancel} className="px-5 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-semibold">İptal</button>
                    <button data-testid="payment-submit" onClick={submit} className="px-6 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm">Ödemeyi Kaydet</button>
                </div>
            </div>
        </div>
    );
}

/* ========== EMAIL SEND MODAL ========== */
function EmailSendModal({ booking, hotel, onCancel, onSubmit }) {
    const guestEmail = booking.guest?.email || "";
    const hotelEmail = hotel?.email || "";
    const [target, setTarget] = useState("guest");
    const [customEmail, setCustomEmail] = useState("");
    const [includePrice, setIncludePrice] = useState(true);
    const [priceMode, setPriceMode] = useState("sale"); // sale | sale_cost | cost — only when hotel
    const [targetEmail, setTargetEmail] = useState(guestEmail);
    const [subject, setSubject] = useState("");
    const [html, setHtml] = useState("");
    const [edited, setEdited] = useState(false);
    const [loading, setLoading] = useState(false);
    const [smtpConfigured, setSmtpConfigured] = useState(false);
    const [mode, setMode] = useState("preview"); // preview | source
    const [sending, setSending] = useState(false);

    // Load preview whenever target / includePrice / priceMode changes
    useEffect(() => {
        setLoading(true);
        const eff = target === "custom" ? "guest" : target;
        api.get(`/admin/bookings/${booking.id}/email-preview`, {
            params: { to: eff, include_price: includePrice, price_mode: priceMode },
        })
            .then((r) => {
                setSubject(r.data.subject);
                setHtml(r.data.html);
                setSmtpConfigured(r.data.smtp_configured);
                setEdited(false);
                if (target === "guest") setTargetEmail(r.data.to_default || guestEmail);
                else if (target === "hotel") setTargetEmail(r.data.to_default || hotelEmail);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line
    }, [target, includePrice, priceMode]);

    const submit = async () => {
        const finalTarget = target === "custom" ? customEmail : targetEmail;
        if (!finalTarget) return toast.error("Hedef e-posta adresi girin");
        setSending(true);
        try {
            await onSubmit({
                to: target,
                custom_email: target === "custom" ? customEmail : undefined,
                target_email: finalTarget,
                include_price: includePrice,
                price_mode: priceMode,
                subject,
                html,
            });
        } finally {
            setSending(false);
        }
    };

    const resetTemplate = async () => {
        setLoading(true);
        try {
            const eff = target === "custom" ? "guest" : target;
            const r = await api.get(`/admin/bookings/${booking.id}/email-preview`, {
                params: { to: eff, include_price: includePrice, price_mode: priceMode },
            });
            setSubject(r.data.subject);
            setHtml(r.data.html);
            setEdited(false);
        } finally {
            setLoading(false);
        }
    };

    const TARGETS = [
        { v: "guest", l: "Misafir", sub: guestEmail || "(yok)" },
        { v: "hotel", l: "Otel", sub: hotelEmail || "(otel e-posta yok)" },
        { v: "custom", l: "Özel adres", sub: "Manuel gir" },
    ];

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={onCancel}>
            <div className="w-full max-w-6xl h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                    <div>
                        <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">Voucher E-postası — Önizleme & Düzenle</h2>
                        {!smtpConfigured && <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">⚠ SMTP henüz yapılandırılmadı — gönder tıklarsanız istek kuyruğa alınır.</p>}
                    </div>
                    <button onClick={onCancel} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                </div>

                <div className="flex-1 flex overflow-hidden">
                    {/* Left: settings */}
                    <div className="w-80 border-r border-slate-200 dark:border-slate-800 p-4 overflow-y-auto space-y-3">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Alıcı türü</div>
                            <div className="space-y-1.5">
                                {TARGETS.map((t) => (
                                    <label key={t.v} data-testid={`email-target-${t.v}`} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${target === t.v ? "bg-sky-50 dark:bg-sky-950/30 border-sky-500" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300"}`}>
                                        <input type="radio" name="target" checked={target === t.v} onChange={() => setTarget(t.v)} className="h-4 w-4 accent-sky-500" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs font-bold">{t.l}</div>
                                            <div className="text-[10px] text-slate-500 truncate">{t.sub}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {target === "custom" ? (
                            <Field label="E-posta">
                                <input data-testid="email-custom" type="email" value={customEmail} onChange={(e) => setCustomEmail(e.target.value)} placeholder="ornek@email.com" className={INPUT} />
                            </Field>
                        ) : (
                            <Field label="Gidecek adres (düzenlenebilir)">
                                <input data-testid="email-target-address" type="email" value={targetEmail} onChange={(e) => setTargetEmail(e.target.value)} className={INPUT} />
                            </Field>
                        )}

                        <Field label="Konu">
                            <input data-testid="email-subject" type="text" value={subject} onChange={(e) => { setSubject(e.target.value); setEdited(true); }} className={INPUT} />
                        </Field>

                        <label className="flex items-center gap-2 p-2 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer">
                            <input type="checkbox" data-testid="email-include-price" checked={includePrice} onChange={(e) => setIncludePrice(e.target.checked)} className="h-4 w-4 accent-sky-500" />
                            <span className="text-xs">Fiyat & ödeme bilgileri dahil</span>
                        </label>

                        {target === "hotel" && includePrice && (
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Otele yansıyan fiyat</div>
                                <div className="grid grid-cols-3 gap-1">
                                    {[
                                        { v: "sale", l: "Satış" },
                                        { v: "sale_cost", l: "Satış + Maliyet" },
                                        { v: "cost", l: "Sadece Maliyet" },
                                    ].map((opt) => (
                                        <button
                                            key={opt.v}
                                            type="button"
                                            onClick={() => setPriceMode(opt.v)}
                                            data-testid={`price-mode-${opt.v}`}
                                            className={`px-2 py-1.5 rounded-lg text-[10px] font-bold transition-colors ${priceMode === opt.v ? "bg-sky-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"}`}
                                        >{opt.l}</button>
                                    ))}
                                </div>
                                <p className="text-[10px] text-slate-500 mt-1 italic">Maliyet rakamı otelin sizinle kontratındaki tutardır (otel görür).</p>
                            </div>
                        )}

                        {edited && (
                            <button onClick={resetTemplate} className="w-full text-xs font-semibold text-amber-600 hover:text-amber-700">↺ Şablonu Sıfırla</button>
                        )}

                        <div className="pt-3 border-t border-slate-100 dark:border-slate-800 text-[10px] text-slate-500 space-y-1">
                            <div><span className="font-bold">Gönderen:</span> rezervasyon@msonlinegroup.com</div>
                            <div><span className="font-bold">PNR:</span> {booking.id.slice(0, 8).toUpperCase()}</div>
                            {target === "hotel" && <div className="text-amber-600 dark:text-amber-400">Otele giderken otomatik <b>Onayla/Reddet</b> token'ı eklenecek.</div>}
                        </div>
                    </div>

                    {/* Right: preview + edit */}
                    <div className="flex-1 flex flex-col bg-slate-50 dark:bg-slate-950 overflow-hidden">
                        <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2">
                            <div className="inline-flex rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                                <button data-testid="mode-preview" onClick={() => setMode("preview")} className={`px-3 py-1.5 text-xs font-bold transition-colors ${mode === "preview" ? "bg-sky-500 text-white" : "bg-white dark:bg-slate-900 text-slate-700"}`}>👁 Önizleme</button>
                                <button data-testid="mode-source" onClick={() => setMode("source")} className={`px-3 py-1.5 text-xs font-bold transition-colors ${mode === "source" ? "bg-sky-500 text-white" : "bg-white dark:bg-slate-900 text-slate-700"}`}>{`</> HTML`}</button>
                            </div>
                            <span className="text-[10px] text-slate-500 ml-auto">Alıcı: <b>{target === "custom" ? customEmail || "—" : targetEmail || "—"}</b></span>
                        </div>

                        <div className="flex-1 overflow-hidden p-4">
                            {loading ? (
                                <div className="h-full flex items-center justify-center text-slate-500 text-sm">Yükleniyor...</div>
                            ) : mode === "preview" ? (
                                <iframe
                                    title="email-preview"
                                    srcDoc={html}
                                    data-testid="email-preview-iframe"
                                    className="w-full h-full bg-white rounded-lg border border-slate-200 dark:border-slate-700 shadow"
                                    sandbox=""
                                />
                            ) : (
                                <textarea
                                    data-testid="email-html-source"
                                    value={html}
                                    onChange={(e) => { setHtml(e.target.value); setEdited(true); }}
                                    className="w-full h-full p-3 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-[11px] font-mono text-slate-800 dark:text-slate-200 resize-none"
                                    spellCheck={false}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-2">
                    <button onClick={onCancel} className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-semibold">İptal</button>
                    <button data-testid="email-submit" onClick={submit} disabled={sending || loading} className="px-6 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold text-sm inline-flex items-center gap-2">
                        <Send className="h-4 w-4" /> {sending ? "Gönderiliyor..." : "Gönder"}
                    </button>
                </div>
            </div>
        </div>
    );
}


// ============ Helpers ============
const FLT_INPUT = "w-full px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-medium text-slate-900 dark:text-white";

// Otel onay durumunu satırda tek tıkla değiştiren buton
function HotelConfirmButton({ booking, onChange }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const status = booking.hotel_confirm_status; // undefined | "sent" | "confirmed" | "rejected"

    const config = {
        undefined: { label: "Otele Gönderilmedi", cls: "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600", icon: "…", title: "Otele henüz gönderilmedi" },
        sent: { label: "Otele Gönderildi", cls: "bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/60", icon: "→", title: "Otele gönderildi, onay bekleniyor" },
        confirmed: { label: "Confirmed", cls: "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/60", icon: "✓", title: "Otel onayladı" },
        rejected: { label: "Not Confirmed", cls: "bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/60", icon: "✗", title: "Otel reddetti" },
    }[status ?? "undefined"];

    const setStatus = async (next, e) => {
        e?.stopPropagation();
        setBusy(true);
        try {
            await api.post(`/admin/bookings/${booking.id}/hotel-confirm`, { status: next });
            toast.success("Otel onay durumu güncellendi");
            setOpen(false);
            onChange && onChange();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Hata");
        } finally { setBusy(false); }
    };

    return (
        <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
                onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
                data-testid={`row-hotel-confirm-${booking.id}`}
                title={config.title}
                className={`w-7 h-7 rounded-md text-xs font-black transition-colors flex items-center justify-center ${config.cls}`}
            >
                {config.icon}
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 z-40 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl min-w-[180px] py-1" data-testid={`row-hotel-confirm-menu-${booking.id}`}>
                    <MenuItem onClick={(e) => setStatus("sent", e)} testid={`hc-set-sent-${booking.id}`} disabled={busy}>→ Otele Gönderildi</MenuItem>
                    <MenuItem onClick={(e) => setStatus("confirmed", e)} testid={`hc-set-confirmed-${booking.id}`} disabled={busy}>✓ Confirmed</MenuItem>
                    <MenuItem onClick={(e) => setStatus("rejected", e)} testid={`hc-set-rejected-${booking.id}`} disabled={busy}>✗ Not Confirmed</MenuItem>
                    <MenuItem onClick={(e) => setStatus("cleared", e)} testid={`hc-set-cleared-${booking.id}`} disabled={busy}>◯ Temizle</MenuItem>
                </div>
            )}
        </div>
    );
}

function MenuItem({ onClick, children, disabled, testid }) {
    return (
        <button onClick={onClick} disabled={disabled} data-testid={testid} className="w-full text-left px-3 py-1.5 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50">
            {children}
        </button>
    );
}

function SummaryCard({ icon, label, value, color = "slate" }) {
    const palette = {
        slate: "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200",
        sky: "bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-900 text-sky-700 dark:text-sky-300",
        emerald: "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-300",
        amber: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-700 dark:text-amber-300",
        rose: "bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300",
        indigo: "bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-900 text-indigo-700 dark:text-indigo-300",
    }[color] || "";
    return (
        <div className={`rounded-xl border p-3 ${palette}`}>
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest opacity-80">
                {icon} {label}
            </div>
            <div className="mt-1 text-base font-bold">{value}</div>
        </div>
    );
}

function FilterField({ label, children }) {
    return (
        <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
            {children}
        </div>
    );
}


